import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Tv, ShieldCheck, ShieldX, ArrowLeft, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * /link — the Plex/YouTube-style "pair your TV" page.
 * 1. User opens torhub.me/link on their phone or laptop while signed in.
 * 2. They type the code shown on the TV (e.g. ABCD-1234).
 * 3. They confirm the device and tap Approve.
 * 4. The TV (which is polling /api/devices/poll) wakes up signed-in.
 */
export default function LinkDevicePage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [code, setCode] = useState((params.get("code") || "").toUpperCase());
  const [lookup, setLookup] = useState(null);        // device info from /lookup
  const [phase, setPhase] = useState("input");        // input | confirm | approved | denied
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  // If not signed in, bounce to login and come back here after.
  useEffect(() => {
    if (!loading && !user) {
      navigate(`/login?next=${encodeURIComponent("/link" + (code ? `?code=${code}` : ""))}`);
    }
  }, [loading, user, navigate, code]);

  // Pretty-format `ABCD1234` as `ABCD-1234` while typing
  const onChange = (raw) => {
    const cleaned = raw.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8);
    setCode(cleaned.length > 4 ? `${cleaned.slice(0, 4)}-${cleaned.slice(4)}` : cleaned);
  };

  const lookupCode = async () => {
    const clean = code.replace(/[^A-Z0-9]/g, "");
    if (clean.length !== 8) {
      toast.error("Enter the 8-character code shown on your TV");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.get("/devices/lookup", { params: { code: clean } });
      setLookup(data);
      setPhase("confirm");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(detail === "code_not_found_or_expired"
        ? "That code is invalid or has expired."
        : "Couldn't look up that code.");
    } finally {
      setBusy(false);
    }
  };

  const approve = async () => {
    setBusy(true);
    try {
      await api.post("/devices/approve", { user_code: code });
      setPhase("approved");
      toast.success("Device linked. Your TV is signing you in now.");
    } catch {
      toast.error("Couldn't approve — try again.");
    } finally {
      setBusy(false);
    }
  };

  const deny = async () => {
    setBusy(true);
    try {
      await api.post("/devices/deny", { user_code: code });
      setPhase("denied");
    } catch {
      toast.error("Could not deny the request.");
    } finally {
      setBusy(false);
    }
  };

  // auto-focus the input
  useEffect(() => { if (phase === "input") inputRef.current?.focus(); }, [phase]);

  if (loading) return null;

  return (
    <div className="min-h-screen bg-[var(--tt-bg)] flex flex-col">
      <Header />
      <div className="flex-1 flex">
        <Sidebar />
        <main className="flex-1 px-4 md:px-8 py-8 max-w-3xl mx-auto w-full">
          <Link to="/settings" className="text-xs text-[var(--tt-text-muted)] hover:text-white inline-flex items-center gap-1 mb-4">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to settings
          </Link>

          <div className="mb-6 flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-[var(--tt-primary)]/15 border border-[var(--tt-primary)]/30 flex items-center justify-center">
              <Tv className="w-6 h-6 text-[var(--tt-primary)]" />
            </div>
            <div>
              <h1 className="font-display text-3xl font-extrabold tracking-tight">Pair your TV</h1>
              <p className="text-sm text-[var(--tt-text-muted)]">
                Enter the code shown on your Fire TV / Android TV to sign in.
              </p>
            </div>
          </div>

          {phase === "input" && (
            <section className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl p-6" data-testid="link-input-card">
              <label className="block text-xs uppercase tracking-wider text-[var(--tt-text-muted)] font-bold mb-2">
                Code from your TV
              </label>
              <input
                ref={inputRef}
                value={code}
                onChange={(e) => onChange(e.target.value)}
                placeholder="ABCD-1234"
                data-testid="link-code-input"
                className="w-full font-mono-tt text-center text-3xl tracking-[0.25em] bg-[var(--tt-surface-2)] border-2 border-[var(--tt-border)] rounded-xl px-4 py-5 outline-none focus:border-[var(--tt-primary)] uppercase"
                onKeyDown={(e) => { if (e.key === "Enter") lookupCode(); }}
                autoComplete="off"
                spellCheck="false"
              />
              <button
                onClick={lookupCode}
                disabled={busy}
                data-testid="link-lookup-btn"
                className="mt-4 w-full bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-40"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                Continue
              </button>
              <p className="text-[11px] text-[var(--tt-text-faint)] mt-4 text-center leading-relaxed">
                Don&apos;t have the TorHub app yet?{" "}
                <Link to="/software#tv" className="underline hover:text-white">Get the Fire TV APK</Link>.
              </p>
            </section>
          )}

          {phase === "confirm" && lookup && (
            <section className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl p-6" data-testid="link-confirm-card">
              <div className="text-xs text-[var(--tt-text-muted)] mb-1">A device is asking to sign in as</div>
              <div className="font-display text-xl font-bold mb-4 text-[var(--tt-primary)]">{user?.email}</div>

              <div className="bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-xl p-4 mb-5">
                <div className="text-[10px] uppercase tracking-wider text-[var(--tt-text-muted)] mb-1">Device</div>
                <div className="text-base font-semibold flex items-center gap-2">
                  <Tv className="w-4 h-4 text-[var(--tt-primary)]" />
                  {lookup.device_name || "Unknown device"}
                </div>
                <div className="text-[11px] text-[var(--tt-text-faint)] font-mono-tt mt-1">
                  {lookup.platform || "android_tv"}{lookup.device_model ? ` · ${lookup.device_model}` : ""} · code {lookup.user_code}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={deny}
                  disabled={busy}
                  data-testid="link-deny-btn"
                  className="bg-[var(--tt-surface-2)] hover:bg-white/10 border border-[var(--tt-border)] text-[var(--tt-text-muted)] hover:text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-40"
                >
                  <ShieldX className="w-4 h-4" /> Not me
                </button>
                <button
                  onClick={approve}
                  disabled={busy}
                  data-testid="link-approve-btn"
                  className="bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-40"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Approve
                </button>
              </div>
              <p className="text-[11px] text-[var(--tt-text-faint)] mt-4 text-center">
                Only approve devices you own. The TV will get a long-lived sign-in token you can revoke anytime in Settings.
              </p>
            </section>
          )}

          {phase === "approved" && (
            <section className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-8 text-center" data-testid="link-approved-card">
              <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
              <div className="font-display text-2xl font-extrabold mb-1">All set.</div>
              <div className="text-sm text-[var(--tt-text-muted)] mb-5">
                Head back to your TV — it should be signing in now.
              </div>
              <Link to="/settings" className="text-xs text-[var(--tt-primary)] underline">
                Manage linked devices
              </Link>
            </section>
          )}

          {phase === "denied" && (
            <section className="bg-red-500/10 border border-red-500/30 rounded-2xl p-8 text-center" data-testid="link-denied-card">
              <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
              <div className="font-display text-2xl font-extrabold mb-1">Request denied.</div>
              <div className="text-sm text-[var(--tt-text-muted)]">
                If you didn&apos;t initiate this, change your password — someone may have tried to pair without your permission.
              </div>
            </section>
          )}
        </main>
      </div>
      <Footer />
    </div>
  );
}
